import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import SolutionCategory from "./SolutionCategory";

import figLogo from "../../assets/logos/fig_CROP.png";
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

const categories = [
    {
        id: "finance",
        title: "Finance & Accounting",
        description:
            "USDA-compliant financial management built for K12 school nutrition. Track every dollar, automate procurement, and generate audit-ready reports.",
        bgColor: "#fff",
        products: [
            {
                name: "FIG Finance",
                logo: figLogo,
                description:
                    "The leading procurement and finance platform for K12 nutrition programs. Live across 8+ district deployments including A5, Flagler, Montgomery, Moore, North Bergen, and Union. Covers vendor management, spending tracking, USDA compliance, and audit-ready reporting.",
            },
            {
                name: "Financial Reporting & Dashboards",
                logo: figLogo,
                description:
                    "Real-time financial dashboards, budget forecasting, and compliance reporting built into every FIG Finance deployment. Visualize spend by category, vendor, and program with drill-down analytics.",
            },
        ],
    },
    {
        id: "nutrition",
        title: "Food & Nutrition Management",
        description:
            "Complete systems for school meal programs — from planning and nutrition analysis to cafeteria point of sale and staff training.",
        bgColor: "#eaf5fa",
        products: [
            {
                name: "Hi5",
                logo: figLogo,
                description:
                    "Food and nutrition management platform. Menu planning, production records, nutrient analysis, and USDA meal pattern compliance — all in one system built for school nutrition directors.",
            },
            {
                name: "POS (Point of Sale)",
                logo: figLogo,
                description:
                    "School cafeteria point-of-sale system. Fast, reliable checkout with real-time meal count tracking, free/reduced eligibility verification, and daily reporting.",
            },
            {
                name: "FIG K12 LMS",
                logo: figLogo,
                description:
                    "Online training modules, certification tracking, corrective actions, and compliance dashboards for nutrition teams. No more spreadsheets — just actionable proof for every review.",
            },
        ],
    },
    {
        id: "procurement",
        title: "Procurement & Audit",
        description:
            "Smarter procurement and audit tools purpose-built for K12 nutrition and operations.",
        bgColor: "#fff",
        products: [
            {
                name: "Bid-FIG",
                logo: swanSolutionsLogo,
                description:
                    "AI-powered bid and procurement analytics. Bid analysis, award recommendations, and compliance verification for child nutrition RFPs. Make better decisions with instant, explainable insights from your bid data.",
            },
            {
                name: "Invoice Audit",
                logo: swanSolutionsLogo,
                description:
                    "Automated invoice review and discrepancy detection. Catch pricing errors, contract mismatches, and duplicate charges before they become audit findings.",
            },
            {
                name: "SFR Audit",
                logo: swanSolutionsLogo,
                description:
                    "School Food Report audit tools. Streamlined compliance verification, documentation management, and review preparation for state and federal audits.",
            },
        ],
    },
    {
        id: "crm",
        title: "CRM & Partner Management",
        description:
            "Relationship management tools for districts, vendors, and EdTech partners.",
        bgColor: "#eaf5fa",
        products: [
            {
                name: "CRM-FIG",
                logo: swanSolutionsLogo,
                description:
                    "District and customer relationship management. Track engagements, manage accounts, coordinate outreach, and maintain a complete history of every interaction across your partner network.",
            },
            {
                name: "Partner CRM",
                logo: swanSolutionsLogo,
                description:
                    "Purpose-built tools for managing EdTech partnerships, vendor relationships, and channel activities. Pipeline tracking, partner onboarding, and co-selling coordination.",
            },
        ],
    },
    {
        id: "ai",
        title: "AI & Custom Development",
        description:
            "From AI-powered analytics to fully custom enterprise software, Swan Solutions is your in-house development team for K12.",
        bgColor: "#fff",
        products: [
            {
                name: "Swan AI Labs",
                logo: swanAiLabsLogo,
                description:
                    "AI-powered RAG solutions, custom LLM chatbots, nutrient and product analysis, document AI, invoice automation, bid analytics, and deep K12 visualization. Real AI for real operational results.",
            },
            {
                name: "Custom Integrations",
                logo: swanSolutionsLogo,
                description:
                    "Secure, scalable APIs and data pipelines to connect SIS, POS, state systems, procurement, HR, and everything else your district depends on. Unlock unified reporting and eliminate manual entry.",
            },
            {
                name: "Custom Software Development",
                logo: swanSolutionsLogo,
                description:
                    "Full-scale enterprise software for K12 districts and EdTech vendors. Finance, workflow, special diets, records, procurement — technology that fits your vision and your budget.",
            },
        ],
    },
];

export default function Solutions() {
    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 16 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background:
                        "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    minHeight: { xs: "40vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 0,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        mb: 2,
                        color: "#fff",
                        fontSize: { xs: "2.2rem", md: "3rem" },
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    Our Solutions
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontWeight: 400,
                        maxWidth: 820,
                        mx: "auto",
                        fontSize: { xs: "1.1rem", md: "1.3rem" },
                        textShadow: "0 2px 14px rgba(20,92,140,0.13)",
                    }}
                >
                    Enterprise software platforms for K12 operations, nutrition, finance,
                    procurement, and AI. Explore every product and service we deliver.
                </Typography>
            </Box>

            {/* Category Sections */}
            {categories.map((cat) => (
                <SolutionCategory
                    key={cat.id}
                    id={cat.id}
                    title={cat.title}
                    description={cat.description}
                    products={cat.products}
                    bgColor={cat.bgColor}
                />
            ))}

            {/* AIOS Teaser */}
            <Box
                sx={{
                    py: { xs: 6, md: 10 },
                    background:
                        "linear-gradient(135deg, #003366 0%, #00CFFE 100%)",
                }}
            >
                <Container maxWidth="md">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Box sx={{ textAlign: "center", color: "#fff" }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 900,
                                    mb: 2,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                AIOS — The Platform Behind It All
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "1.15rem",
                                    maxWidth: 700,
                                    mx: "auto",
                                    mb: 4,
                                    color: "rgba(255,255,255,0.92)",
                                }}
                            >
                                Every system above is connected, coordinated, and made smarter by
                                AIOS — our Agentic Operating System. AIOS manages 40+ databases,
                                orchestrates AI agents, and provides federated intelligence across
                                the entire Swan ecosystem.
                            </Typography>
                            <Button
                                href="/aios"
                                variant="contained"
                                size="large"
                                component={motion.a}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.97 }}
                                sx={{
                                    borderRadius: 99,
                                    fontWeight: 700,
                                    px: 5,
                                    py: 1.4,
                                    bgcolor: "#fff",
                                    color: "#003366",
                                    fontSize: "1.12rem",
                                    "&:hover": {
                                        bgcolor: "rgba(255,255,255,0.9)",
                                    },
                                }}
                            >
                                Learn About AIOS
                            </Button>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box sx={{ textAlign: "center", py: { xs: 6, md: 10 }, bgcolor: "#eaf8fb" }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Your Vision. Our Technology.
                    </Typography>
                    <Typography
                        sx={{
                            maxWidth: 650,
                            mx: "auto",
                            color: "text.secondary",
                            fontSize: "1.13rem",
                            mb: 3,
                        }}
                    >
                        Swan Solutions is your partner for practical, reliable, and future-ready
                        software. Let's build something that transforms your district or business.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href="/contact"
                        component={motion.a}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        sx={{
                            borderRadius: 99,
                            fontWeight: 700,
                            fontSize: "1.15rem",
                            py: 1.5,
                            px: 5,
                            mt: 1,
                            background: "linear-gradient(90deg, #145c8c, #21dbb8)",
                        }}
                    >
                        Start the Conversation
                    </Button>
                </motion.div>
            </Box>
        </Box>
    );
}
